import { useCallback, useMemo } from 'react';

import useMerchant from './useMerchant';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { TransactionOwnershipType } from 'src/resources/api/purchase/types.api';
import { useQueryProgrammesList } from 'src/resources/queries/programmes.queries';
import { Programme, ProgrammeType } from 'src/resources/api/programme/types.api';
import { useQueryBasketByCodeGet, useQueryPreferredBasketGet } from 'src/resources/queries';

//
//

export const useProgramme = (code?: string | null) => {
  const { merchant } = useMerchant();

  const getProgrammesHook = useQueryProgrammesList({
    variables: { merchantCode: merchant?.code || '' },
    enabled: !!merchant?.code
  });

  const programmes = useMemo(() => {
    return getProgrammesHook?.data?.content || []
  }, [getProgrammesHook?.data?.content])

  const currentProgramme = useMemo(() => {
    return programmes?.find((programme) => programme?.code === code);
  }, [code, programmes]);

  const getProgrammeLink = useCallback((purchase?: any) => {
    if (!purchase?.id) return '';
  
    const programme = programmes?.find((p: Programme) => p?.code === purchase?.programmeCode);
  
    if (purchase?.programmeCode && programme?.type) {
      return PATH_DASHBOARD.programs.transaction(programme?.type.toLowerCase(), purchase?.programmeCode, purchase?.id)
    } else {
      return PATH_DASHBOARD.offsets.transaction(purchase.id)
    }
  }, [programmes]);

  // TODO: Remove temporal fix when backend handles ownership types by itself
  const ownershipTypeBasedOnProgrammeType: TransactionOwnershipType[] = useMemo(() => {
    switch (currentProgramme?.type) {
      case ProgrammeType.MYCARBONCLICK:
        return [TransactionOwnershipType.CUSTOMER];
      case ProgrammeType.MANAGED:
      case ProgrammeType.CUSTOMER:
        return [TransactionOwnershipType.CUSTOMER, TransactionOwnershipType.MERCHANT];
      default:
        return []
    }
  }, [currentProgramme?.type]);

  //

  const getPreferredBasketHook = useQueryPreferredBasketGet({
    variables: { merchantCode: merchant?.code!, programmeCode: currentProgramme?.code, currency: merchant?.currency },
    enabled: !!merchant?.code && !!currentProgramme,
  });

  const prefferedBasketCode = useMemo(() => {
    if (currentProgramme?.preferredBasketCode) {
      return currentProgramme?.preferredBasketCode;
    } else if (merchant?.preferredBasketCode) {
      return merchant?.preferredBasketCode;
    } else {
      return getPreferredBasketHook?.data?.code;
    }
  }, [currentProgramme?.preferredBasketCode, getPreferredBasketHook?.data?.code, merchant?.preferredBasketCode]);

  const useDefaultBasketCode = useMemo(() => {
   if (merchant?.preferredBasketCode) {
      return merchant?.preferredBasketCode;
    } else {
      return getPreferredBasketHook?.data?.code;
    }
  }, [getPreferredBasketHook?.data?.code, merchant?.preferredBasketCode]);

  const getBasketByCodeHook = useQueryBasketByCodeGet({
    variables: { code: prefferedBasketCode },
    enabled: !!prefferedBasketCode,
  });

  const getDefaultBasketByCodeHook = useQueryBasketByCodeGet({
    variables: { code: useDefaultBasketCode },
    enabled: !!useDefaultBasketCode,
  });

  //

  return {
    programme: currentProgramme,
    prefferedBasketAsync: getBasketByCodeHook,
    preferredUseDefaultAsync: getDefaultBasketByCodeHook,
    isLoading: getProgrammesHook?.isPending,
    programmes,
    getProgrammeLink,
    ownershipTypeBasedOnProgrammeType,
  };
};
